import React from "react";

import { useTranslation } from "react-i18next";

import { StandardLayout } from "@/components/layouts";
import { FilterOptions, Pagination } from "@/components/molecules";
import { IFilterItem } from "@/shared/contexts/localFilterContext";
import { PageParams } from "@/shared/domain";
import { StyledListEmpty } from "@/styles/styled";

import {
    StyledPaginatedList,
    StyledPaginatedListPage,
    StyledPaginatedProgressBar,
    StyledPaginatedSeparator,
} from "./styled";

type PaginatedListProps<ObjectType> = {
    page: PageParams;
    items: ObjectType[];
    setPage: (value: number) => void;
    category?: string;
    renderItem?: (item: ObjectType) => React.ReactElement;
    renderTable: (items: ObjectType[]) => React.ReactElement;
    totalOfPages: number;
    setCategory?: (value: string) => void;
    pageTitle: string;
    availableFilters?;
    localFilters?: IFilterItem[];
    emptyListMessage: string;
    message?: React.ReactElement;
    handlePageSizeChange: (value: number) => void;
    openSearch?: () => void;
    openLinkedDocuments?: () => void;
    isFetchingList: boolean;
    isLoading: boolean;
    tableMode: boolean;
    hideGlobalFilters?: boolean;
    alwaysShowFilters?: boolean;
    displayLinkedDocumentsButton?: boolean;
};

const PaginatedList = <ObjectType,>({
    page,
    items,
    setPage,
    message,
    category,
    pageTitle,
    isLoading,
    tableMode,
    openSearch,
    renderItem,
    renderTable,
    setCategory,
    localFilters,
    totalOfPages,
    isFetchingList,
    availableFilters = [],
    emptyListMessage,
    openLinkedDocuments,
    handlePageSizeChange,
    alwaysShowFilters = false,
    hideGlobalFilters = false,
    displayLinkedDocumentsButton = false,
}: PaginatedListProps<ObjectType>) => {
    const { t } = useTranslation();

    const isEmpty = items.length === 0;

    const getResults = () => {
        if (isEmpty && isLoading) {
            return <StyledListEmpty data-testid="empty-paginated-list">{t("Common.Loading")}</StyledListEmpty>;
        }
        if (isEmpty) {
            return <StyledListEmpty data-testid="empty-paginated-list">{emptyListMessage}</StyledListEmpty>;
        }

        if (items.length > 0) {
            if (tableMode) {
                return renderTable(items);
            }

            return items.map((item) => renderItem(item));
        }

        return null;
    };

    const handleChangeCategory = (newCategory: string) => {
        setCategory(newCategory);
        setPage(1);
    };

    const hasFilters = (!isEmpty || alwaysShowFilters) && availableFilters.length > 0 && !!setCategory;

    return (
        <StandardLayout
            title={pageTitle}
            showGlobalFilter={!hideGlobalFilters}
            onOpenSearch={openSearch}
            localFilters={localFilters}
            onOpenLinkedDocuments={openLinkedDocuments}
            displayLinkedDocumentsButton={displayLinkedDocumentsButton}
        >
            <StyledPaginatedListPage>
                {!isLoading && message}
                {hasFilters && (
                    <FilterOptions
                        selectedFilterId={category}
                        availableFilters={availableFilters}
                        onChangeFilter={handleChangeCategory}
                    />
                )}
                <StyledPaginatedProgressBar $isLoading={isLoading} classes={{ barColorPrimary: "barColorPrimary" }} />
                {hasFilters && <StyledPaginatedSeparator $isLoading={isLoading} />}
                <StyledPaginatedList data-testid="paginated-list">{getResults()}</StyledPaginatedList>
                <Pagination
                    page={page}
                    tableMode={tableMode}
                    onPageChange={setPage}
                    pageCount={totalOfPages}
                    disabled={isFetchingList}
                    onPageSizeChange={handlePageSizeChange}
                />
            </StyledPaginatedListPage>
        </StandardLayout>
    );
};

export default PaginatedList;
